import { twJoin } from 'tailwind-merge';

import Button from 'molecules/Button';
import Icon from 'molecules/Icon';
import Link from 'molecules/Link';

import MiniCallOutCard from 'layout/header/components/MiniCallOutCard';
import { closeMenu } from 'layout/header/store';
import { menuItemListStyle } from 'layout/header/styles';

import { hasArrayValues } from 'utils/array/arrays';

import type { IconIds } from 'molecules/Icon/types';
import type { FC } from 'react';

const Menu: FC<HeaderMegaMenuMenu & { direction: 'column' | 'row'; isMobile?: boolean }> = ({
  label,
  items,
  direction,
  isMobile,
  viewAll,
  miniCallOuts,
}) => {
  const hasIcons = items?.some(item => item.icon);
  const isTextList = !hasIcons;
  const hasCards = hasArrayValues(miniCallOuts);
  // The compact horizontal promo layout is only used when a column has multiple
  // promo cards (e.g. the Resources dropdown). A single promo card (e.g. the
  // Platform dropdown) keeps the original standalone layout.
  const isPromoGrid = hasCards && miniCallOuts.length > 1;

  return (
    <div
      className={`flex flex-col ${
        isMobile ? 'gap-3 py-0' : `shrink-0 gap-3 py-4 ${isPromoGrid ? 'xl:w-[300px]' : 'min-w-[180px]'}`
      }`}
    >
      {label && <span className="!font-spaceGrotesk text-xl font-bold leading-[30px] text-white">{label}</span>}
      {hasArrayValues(items) && (
        <div className={isMobile ? 'px-3' : ''}>
          {isTextList ? (
            <div className="flex flex-col gap-3 xl:mt-4">
              {items.map(item => (
                <Link
                  key={item.label}
                  href={item.link}
                  className="text-sm leading-5 text-white hover:text-primary-100"
                  onClick={() => closeMenu()}
                  enablePreview={false}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          ) : (
            <div className={`flex flex-col gap-3 ${isMobile ? '' : menuItemListStyle({ direction })}`}>
              {items.map(item => (
                <Link
                  key={item.label}
                  href={item.link}
                  className="group flex items-center gap-3 rounded border border-transparent bg-transparent p-1 transition-all duration-200 hover:border-primary-100/10 hover:bg-white/5"
                  onClick={() => closeMenu()}
                  enablePreview={false}
                >
                  <>
                    <span
                      className="flex size-9 shrink-0 basis-9 items-center justify-center rounded bg-icon stroke-2 p-2 text-gray-900
                      [shape-rendering:geometricPrecision] [text-rendering:geometricPrecision]"
                    >
                      {item.icon && <Icon icon={item.icon as IconIds} size={20} />}
                    </span>
                    <span className="flex flex-1 flex-col">
                      <span
                        className={twJoin(
                          'text-white group-hover:text-primary-100',
                          item.description ? 'text-md font-semibold' : 'text-sm font-medium',
                        )}
                      >
                        {item.label}
                      </span>
                      {item.description && (
                        <span className="max-w-[305px] text-xs text-gray-300 group-hover:text-primary-100">
                          {item.description}
                        </span>
                      )}
                    </span>
                  </>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
      {hasCards &&
        miniCallOuts.map(miniCallOut => (
          <MiniCallOutCard {...miniCallOut} context={isPromoGrid ? 'inside' : 'standalone'} key={miniCallOut.heading} />
        ))}
      {viewAll?.label && (
        <div className={isMobile ? 'px-3' : 'mt-2'}>
          <Button
            label={viewAll.label}
            href={viewAll.link}
            hierarchy="linkGray"
            className="w-fit !text-md font-semibold"
            endIcon="arrow-up-right"
            onClick={() => closeMenu()}
          />
        </div>
      )}
    </div>
  );
};

export default Menu;
